import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Eye, EyeOff, Key, AlertTriangle } from "lucide-react";
import type { UserInfo } from "@/types/superAdmin";
import { useTranslation } from "react-i18next";

interface ForcePasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: UserInfo | null;
  onConfirm: (userId: string, password: string, signOutAll: boolean, sendEmail: boolean) => void;
  isLoading: boolean;
}

export function ForcePasswordDialog({
  open,
  onOpenChange,
  user,
  onConfirm,
  isLoading,
}: ForcePasswordDialogProps) {
  const { t } = useTranslation(["superadmin", "common"]);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [signOutAll, setSignOutAll] = useState(true);
  const [sendEmail, setSendEmail] = useState(true);

  const passwordsMatch = newPassword === confirmPassword;
  const passwordValid = newPassword.length >= 8;
  const canSubmit = passwordValid && passwordsMatch && !isLoading;

  const handleConfirm = () => {
    if (!user || !canSubmit) return;
    onConfirm(user.user_id, newPassword, signOutAll, sendEmail);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      // Reset form when closing
      setNewPassword("");
      setConfirmPassword("");
      setShowPassword(false);
      setSignOutAll(true);
      setSendEmail(true);
    }
    onOpenChange(open);
  };

  if (!user) return null;

  const userName = `${user.first_name} ${user.last_name}`.trim() || user.email;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Key className="h-5 w-5 text-orange-500" />
            {t("forcePassword.title")}
          </DialogTitle>
          <DialogDescription>
            {t("forcePassword.setNewPasswordFor")} <strong>{userName}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Warning */}
          <div className="flex items-start gap-2 p-3 bg-orange-500/10 border border-orange-500/20 rounded-lg">
            <AlertTriangle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-muted-foreground">
              {t("forcePassword.warning")}
            </p>
          </div>

          {/* New Password */}
          <div className="space-y-2">
            <Label htmlFor="newPassword">{t("forcePassword.newPassword")}</Label>
            <div className="relative">
              <Input
                id="newPassword"
                type={showPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder={t("forcePassword.minChars")}
                className="pr-10"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <Eye className="h-4 w-4 text-muted-foreground" />
                )}
              </Button>
            </div>
            {newPassword && !passwordValid && (
              <p className="text-sm text-destructive">
                {t("forcePassword.passwordTooShort")}
              </p>
            )}
          </div>

          {/* Confirm Password */}
          <div className="space-y-2">
            <Label htmlFor="confirmPassword">{t("forcePassword.confirmPassword")}</Label>
            <Input
              id="confirmPassword"
              type={showPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder={t("forcePassword.repeatPassword")}
            />
            {confirmPassword && !passwordsMatch && (
              <p className="text-sm text-destructive">
                {t("forcePassword.passwordsDontMatch")}
              </p>
            )}
          </div>

          {/* Options */}
          <div className="space-y-3 pt-2">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="signOutAll"
                checked={signOutAll}
                onCheckedChange={(checked) => setSignOutAll(checked as boolean)}
              />
              <Label htmlFor="signOutAll" className="text-sm font-normal cursor-pointer">
                {t("forcePassword.signOutAllDevices")}
              </Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="sendEmail"
                checked={sendEmail}
                onCheckedChange={(checked) => setSendEmail(checked as boolean)}
              />
              <Label htmlFor="sendEmail" className="text-sm font-normal cursor-pointer">
                {t("forcePassword.sendByEmail")}
              </Label>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isLoading}>
            {t("common:cancel")}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!canSubmit}
            className="bg-orange-500 hover:bg-orange-600"
          >
            {isLoading ? t("forcePassword.inProgress") : t("common:confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
